import React, { useRef, useEffect } from 'react';
import { SearchResult } from './useGlobalSearch';
import SearchResultItem from './SearchResultItem';
import Spinner from '../Spinner/Spinner';

interface SearchAutocompleteProps {
  isOpen: boolean;
  results: SearchResult[];
  isLoading: boolean;
  selectedIndex: number;
  onSelect: (result: SearchResult) => void;
  query: string;
  className?: string;
}

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  isOpen,
  results,
  isLoading,
  selectedIndex,
  onSelect,
  query,
  className,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (dropdownRef.current && selectedIndex >= 0) {
      const selectedElement = dropdownRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={className || "absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto z-50"}
    >
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Spinner className="h-4 w-4" />
          <span className="ml-2 text-sm text-gray-500">Searching...</span>
        </div>
      )}
      
      {!isLoading && results.length === 0 && query.length >= 2 && (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          No results found for &quot;{query}&quot;
        </div>
      )}
      
      {!isLoading && results.length > 0 && (
        <ul className="py-1">
          {results.map((result, index) => (
            <SearchResultItem
              key={`${result.type}-${result.id}`}
              result={result}
              isSelected={index === selectedIndex}
              onClick={() => onSelect(result)}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchAutocomplete;